import { useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Info, Star, HelpCircle, BookOpen, Users2, Image,
  MessageSquareQuote, CalendarDays, ClipboardCheck, Phone, Menu as MenuIcon,
  PanelBottom, Palette, Search, Inbox, FolderOpen, Users, Activity, LogOut,
  Mic, X, ChevronRight, Newspaper, MousePointerClick, GalleryHorizontal, LayoutTemplate,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SectionEditor from "./components/SectionEditor";
import ListManager from "./components/ListManager";
import Overview from "./pages/Overview";
import Submissions from "./pages/Submissions";
import MediaLibrary from "./pages/MediaLibrary";
import UsersPage from "./pages/Users";
import ActivityLog from "./pages/Activity";
import BlogManager from "./pages/BlogManager";
import SectionsBuilder from "./pages/SectionsBuilder";

const TEMPLATES = {
  courses: { icon: "BookOpen", title: "নতুন বিভাগ", age: "", desc: "", fees: "", duration: "", image: "", featured: false },
  faculty: { name: "নতুন শিক্ষক", role: "", bio: "", photo: "", socials: [] },
  gallery: { image: "", caption: "", category: "" },
  testimonials: { quote: "", name: "", role: "অভিভাবক", rating: 5, photo: "" },
  events: { title: "নতুন অনুষ্ঠান", date: "", desc: "", venue: "", image: "", regLink: "#admission" },
  popups: { title: "নতুন পপআপ", type: "image", image: "", heading: "শিরোনাম", text: "", buttonLabel: "বিস্তারিত", buttonHref: "#admission", showDelay: "3", frequency: "session", enabled: false },
  banners: { title: "নতুন ব্যানার", image: "", heading: "শিরোনাম", subheading: "", buttonLabel: "বিস্তারিত", buttonHref: "#admission", overlay: "0.45", enabled: true },
};

const NAV = [
  { group: "সাধারণ", items: [
    { to: "", label: "ড্যাশবোর্ড", icon: LayoutDashboard, end: true },
    { to: "sections", label: "পেজ বিল্ডার", icon: LayoutTemplate },
    { to: "theme", label: "থিম কাস্টমাইজার", icon: Palette },
    { to: "branding", label: "ব্র্যান্ডিং ও লোগো", icon: Sparkles },
    { to: "menu", label: "নেভিগেশন মেনু", icon: MenuIcon },
    { to: "seo", label: "SEO সেটিংস", icon: Search },
  ]},
  { group: "পেজ সেকশন", items: [
    { to: "hero", label: "হিরো", icon: Sparkles },
    { to: "stats", label: "পরিসংখ্যান", icon: Star },
    { to: "about", label: "পরিচয়", icon: Info },
    { to: "why", label: "কেন শ্রুতি", icon: HelpCircle },
    { to: "admission", label: "ভর্তি সেকশন", icon: ClipboardCheck },
    { to: "contact", label: "যোগাযোগ", icon: Phone },
    { to: "footer", label: "ফুটার", icon: PanelBottom },
  ]},
  { group: "কনটেন্ট", items: [
    { to: "courses", label: "পাঠক্রম", icon: BookOpen },
    { to: "faculty", label: "শিক্ষকমণ্ডলী", icon: Users2 },
    { to: "gallery", label: "গ্যালারি", icon: Image },
    { to: "testimonials", label: "প্রশংসাপত্র", icon: MessageSquareQuote },
    { to: "events", label: "অনুষ্ঠান", icon: CalendarDays },
    { to: "blog", label: "ব্লগ", icon: Newspaper },
  ]},
  { group: "মার্কেটিং", items: [
    { to: "popups", label: "পপআপ বিল্ডার", icon: MousePointerClick },
    { to: "banners", label: "ব্যানার স্লাইডার", icon: GalleryHorizontal },
  ]},
  { group: "ব্যবস্থাপনা", items: [
    { to: "submissions", label: "ফর্ম জমা", icon: Inbox },
    { to: "media", label: "মিডিয়া লাইব্রেরি", icon: FolderOpen },
    { to: "users", label: "ব্যবহারকারী", icon: Users },
    { to: "activity", label: "কার্যকলাপ লগ", icon: Activity },
  ]},
];

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-stone-100 flex" style={{ "--c-primary": "#6A1B1A", "--c-secondary": "#CC583F", "--c-accent": "#BFA15F" }}>
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-72 bg-[#3a1211] text-stone-200 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="flex items-center justify-between px-6 h-[70px] border-b border-white/10">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full border border-[var(--c-accent)]"><Mic className="h-4 w-4" /></span>
            <div><div className="font-heading text-xl font-bold text-white">শ্রুতি</div><div className="text-[9px] tracking-[0.3em] text-[var(--c-accent)]">ADMIN CMS</div></div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-white/60"><X className="h-5 w-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {NAV.map((g) => (
            <div key={g.group}>
              <div className="px-3 text-[10px] uppercase tracking-widest text-white/30 mb-2">{g.group}</div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to ? `/admin/${it.to}` : "/admin"}
                    end={it.end}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${it.to || "dashboard"}`}
                    className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[var(--c-secondary)] text-white" : "text-stone-300 hover:bg-white/5"}`}
                  >
                    <it.icon className="h-4 w-4" strokeWidth={1.5} /> {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[var(--c-accent)] text-white font-heading">{(user?.name || "শ")[0]}</span>
            <div className="min-w-0"><div className="text-sm text-white truncate">{user?.name}</div><div className="text-xs text-white/40 truncate">{user?.email}</div></div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-sm text-stone-300">
            <LogOut className="h-4 w-4" /> লগআউট
          </button>
        </div>
      </aside>

      {open && <div onClick={() => setOpen(false)} className="fixed inset-0 z-30 bg-black/40 lg:hidden" />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-stone-200 h-[60px] flex items-center px-4 gap-3">
          <button onClick={() => setOpen(true)}><MenuIcon className="h-6 w-6 text-stone-700" /></button>
          <span className="font-heading text-lg font-bold text-[var(--c-primary)]">শ্রুতি অ্যাডমিন</span>
        </header>
        <main className="p-5 sm:p-8">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="theme" element={<SectionEditor sectionKey="theme" title="থিম কাস্টমাইজার" description="রঙ, ফন্ট ও কোণের গোলাই পরিবর্তন করুন" />} />
            <Route path="branding" element={<SectionEditor sectionKey="branding" title="ব্র্যান্ডিং ও লোগো" description="প্রতিষ্ঠানের নাম ও ট্যাগলাইন" />} />
            <Route path="menu" element={<SectionEditor sectionKey="menu" title="নেভিগেশন মেনু" description="উপরের মেনুর আইটেমসমূহ" />} />
            <Route path="seo" element={<SectionEditor sectionKey="seo" title="SEO সেটিংস" description="মেটা টাইটেল ও বিবরণ" />} />
            <Route path="hero" element={<SectionEditor sectionKey="hero" title="হিরো সেকশন" description="মূল ব্যানার" />} />
            <Route path="stats" element={<SectionEditor sectionKey="stats" title="পরিসংখ্যান" description="হিরোর নিচের সংখ্যাগুলো" />} />
            <Route path="about" element={<SectionEditor sectionKey="about" title="পরিচয় সেকশন" />} />
            <Route path="why" element={<SectionEditor sectionKey="why" title="কেন শ্রুতি" />} />
            <Route path="admission" element={<SectionEditor sectionKey="admission" title="ভর্তি সেকশন" />} />
            <Route path="contact" element={<SectionEditor sectionKey="contact" title="যোগাযোগ সেকশন" />} />
            <Route path="footer" element={<SectionEditor sectionKey="footer" title="ফুটার" />} />
            <Route path="courses" element={<ListManager name="courses" title="পাঠক্রম" template={TEMPLATES.courses} headerKey="coursesSection" headerTitle="পাঠক্রম শিরোনাম" />} />
            <Route path="faculty" element={<ListManager name="faculty" title="শিক্ষকমণ্ডলী" template={TEMPLATES.faculty} headerKey="facultySection" headerTitle="শিক্ষক শিরোনাম" />} />
            <Route path="gallery" element={<ListManager name="gallery" title="গ্যালারি" template={TEMPLATES.gallery} headerKey="gallerySection" headerTitle="গ্যালারি শিরোনাম" />} />
            <Route path="testimonials" element={<ListManager name="testimonials" title="প্রশংসাপত্র" template={TEMPLATES.testimonials} headerKey="testimonialsSection" headerTitle="প্রশংসা শিরোনাম" />} />
            <Route path="events" element={<ListManager name="events" title="অনুষ্ঠান" template={TEMPLATES.events} headerKey="eventsSection" headerTitle="অনুষ্ঠান শিরোনাম" />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="popups" element={<ListManager name="popups" title="পপআপ বিল্ডার" description="নির্ধারিত সময়ে দেখানো পপআপ তৈরি করুন (type: image/admission/event, frequency: session/once/always)" template={TEMPLATES.popups} />} />
            <Route path="banners" element={<ListManager name="banners" title="ব্যানার স্লাইডার" description="হোমপেজের উপরে দেখানো ব্যানার স্লাইড" template={TEMPLATES.banners} />} />
            <Route path="sections" element={<SectionsBuilder />} />
            <Route path="submissions" element={<Submissions />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="activity" element={<ActivityLog />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
